import { useState } from 'react'
import { useNavigate } from 'react-router'
import { apiOrigin } from '../../lib/api.ts'
import { useModuleGates } from '../../lib/module-gate.tsx'
import { WorkspaceSection, useWorkspaces, type Workspace } from './WorkspaceMenu.tsx'

/**
 * Identidade do workspace no shell.
 *
 * O LOGO substitui a marca Orbind no topo do rail (pedido do usuario,
 * 2026-08-14); o NOME com a setinha fica no topo do explorer de qualquer
 * modulo — inclusive nos que trazem explorer proprio (Itens de Trabalho,
 * Quadros), por isso este componente e compartilhado e o estilo mora no
 * shell.css.
 *
 * O menu traz a TROCA de workspace + criar (pedido do usuario, 2026-08-15 —
 * antes a lista vivia so no popover de perfil e ninguem achava) e o atalho
 * "Editar workspace"; renomear e logo seguem morando na tela /workspace.
 */

/** Url publica do logo; `logoVersion` muda a cada alteracao e fura o cache. */
export function workspaceLogoUrl(w: Pick<Workspace, 'id' | 'logoVersion'>, variante?: 'clara' | 'escura') {
  // Tema escuro/Black pede a logo escura (SPEC-20261104-1523); o servidor cai
  // na clara quando nao ha. `variante` forca uma delas (tela de edicao).
  const tema = typeof document === 'undefined' ? '' : document.documentElement.dataset.theme ?? ''
  const escura = variante ? variante === 'escura' : tema === 'dark' || tema === 'black'
  return `${apiOrigin()}/public/workspace-logo/${w.id}?v=${encodeURIComponent(w.logoVersion ?? '')}${escura ? '&tema=escuro' : ''}`
}

/**
 * Sigla do workspace para quando ele NAO tem logo (pedido 2026-08-26): 2 letras
 * — as iniciais das duas primeiras palavras, ou as 2 primeiras letras de um
 * nome de palavra unica. Sempre mostra em qual workspace se esta.
 */
export function siglaDoWorkspace(name: string): string {
  const palavras = name.trim().split(/\s+/).filter(Boolean)
  const primeira = palavras[0]
  if (!primeira) return '?'
  const segunda = palavras[1]
  if (!segunda) return primeira.slice(0, 2).toUpperCase()
  return `${primeira[0]}${segunda[0]}`.toUpperCase()
}

export function useCurrentWorkspace(): Workspace | null {
  const { data } = useWorkspaces()
  return data?.find((w) => w.isCurrent) ?? null
}

export function WorkspaceBar() {
  const navigate = useNavigate()
  const ws = useCurrentWorkspace()
  const [menuOpen, setMenuOpen] = useState(false)
  // Administração mora aqui, abaixo de "Editar workspace" (SPEC-20261104-1422)
  const { moduleEnabled } = useModuleGates()

  if (!ws) return null

  return (
    <div className="wsbar">
      {/* O NOME e o gatilho do menu (padrao Slack): sem botao separado. */}
      <button
        type="button"
        className="wsbar__trigger"
        aria-label={`Ações do workspace ${ws.name}`}
        aria-expanded={menuOpen}
        title={`Workspace ${ws.name} — clique para ações`}
        onClick={() => setMenuOpen((aberto) => !aberto)}
      >
        <span className="wsbar__name">{ws.name}</span>
        <i className="fas fa-chevron-down wsbar__caret" aria-hidden />
      </button>

      {menuOpen ? (
        <>
          <div className="shell__popover-scrim" onClick={() => setMenuOpen(false)} aria-hidden />
          <div className="wsbar__menu" role="menu" aria-label={`Ações do workspace ${ws.name}`}>
            {/* trocar de workspace / criar um novo — a sessao reemite o token */}
            <WorkspaceSection
              onCriar={() => {
                setMenuOpen(false)
                navigate('/workspace/new')
              }}
            />
            <button
              type="button"
              role="menuitem"
              className="shell__popover-item"
              onClick={() => {
                setMenuOpen(false)
                navigate('/workspace')
              }}
            >
              <i className="fas fa-cog" aria-hidden />
              Editar workspace
            </button>
            {moduleEnabled('admin') ? (
              <button
                type="button"
                role="menuitem"
                className="shell__popover-item"
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/admin')
                }}
              >
                <i className="fas fa-sliders-h" aria-hidden />
                Administração
              </button>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  )
}
